// Claim/release discipline for dockStore's single global slots.
//
// dockStore is last-writer-wins with no ownership, and in dashboard mode two
// writer surfaces are mounted at once: ScopedPage/ScopedView stay mounted
// (hidden + inert) under the overlay while CanvasHomePage owns the dock. The
// invariant that keeps that safe is exactly one rule — A HIDDEN SURFACE OWNS
// NOTHING: it must not claim a slot, and it must not release a slot it no
// longer owns. Violating it is how `?dcol=open` used to make a hidden
// ScopedPage clobber the home page's claim and permanently unpublish the chat
// sidebar. These hooks/factory are that rule, extracted from the pages so it
// is unit-testable; the pages wire them to their own visibility.
import { useCallback, useEffect, useRef } from "react";
import {
    getDockBodyHost,
    setDockAvailable,
    setDockBodyHost,
    setDockColumnExpanded,
    setDockHostPage,
} from "./dockStore.js";

// dockAvailable claim: while visible, publish `available` and release on
// change/unmount; while hidden, write nothing — including no cleanup release,
// since any claim the slot holds belongs to another (visible) surface.
export function useDockAvailabilityClaim(visible, available) {
    useEffect(() => {
        if (!visible) return undefined;
        setDockAvailable(available);
        return () => setDockAvailable(false);
    }, [visible, available]);
}

// dockHostPage claim: a visible dock host page says so for as long as it is
// on screen, whatever its column's state; hidden, it writes nothing.
export function useDockHostPageClaim(visible) {
    useEffect(() => {
        if (!visible) return undefined;
        setDockHostPage(true);
        return () => setDockHostPage(false);
    }, [visible]);
}

// columnExpanded publisher: same hidden-page rule. No cleanup — the slot is a
// plain mirror of the visible owner's state, and the next visible owner
// overwrites it.
export function useDockColumnExpandedPublisher(visible, expanded) {
    useEffect(() => {
        if (!visible) return;
        setDockColumnExpanded(expanded);
    }, [visible, expanded]);
}

// bodyHost binding for a callback ref: claim the node only while visible,
// and on detach release ONLY if this surface still owns the slot — a hidden
// page's unmount null must not clobber another visible surface's claim.
// `reclaim` re-asserts the held node (the caller gates it on its own
// visibility/mode), for the surface coming back from under the overlay.
export function createDockBodyHostBinding(isVisible) {
    let node = null;
    return {
        ref(el) {
            if (el) {
                node = el;
                if (isVisible()) setDockBodyHost(el);
            } else {
                if (node != null && getDockBodyHost() === node) setDockBodyHost(null);
                node = null;
            }
        },
        reclaim() {
            if (node != null && isVisible()) setDockBodyHost(node);
        },
        hasNode: () => node != null,
    };
}

// React wiring for the binding: a stable callback ref whose visibility check
// reads the CURRENT prop, not the value captured at mount.
export function useDockBodyHostRef(visible) {
    const visibleRef = useRef(visible);
    useEffect(() => { visibleRef.current = visible; }, [visible]);
    const bindingRef = useRef(null);
    if (bindingRef.current === null) {
        bindingRef.current = createDockBodyHostBinding(() => visibleRef.current);
    }
    const binding = bindingRef.current;
    const ref = useCallback((el) => binding.ref(el), [binding]);
    return { ref, reclaim: binding.reclaim };
}
